import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { fetchOrders, updateOrderStatus } from '../../../store/CommonSlice';
import type { AppDispatch } from '../../../store/store';
import { CartItem, Delivery, Order as OrderItem, ThreePlusPromoMeta } from '../../../types/common';
import { formatPrice, getOrderStatusData, orderStatuses } from '../../../utils/helpers';
import OrderCartItem from './OrderCartItem/OrderCartItem';
import OrderMarkingPopup, { OrderMarkingRow } from './OrderMarkingPopup/OrderMarkingPopup';
import { getCsrfToken } from '../../../utils/helpers';
import classes from './Order.module.css';

type PromoDiscountType = 'percentage' | 'points';
interface PromoCodeRecord {
    code: string;
    discountType: PromoDiscountType;
    discountValue: number | string;
    status?: 'active' | 'inactive';
}
type SecondPillowPromoMeta = {
    key: string;
    discountAmount: number;
    validUntil?: string | null;
    categoryUrl?: string | null;
};
interface IOrderProps {
    id: string;
    user: OrderItem['user'];
    timestamp: OrderItem['timestamp'];
    totalPrice: OrderItem['totalPrice'];
    totalDiscount: OrderItem['totalDiscount'];
    totalWeight: OrderItem['totalWeight'];
    birthdayDiscount: OrderItem['birthdayDiscount'];
    paidWithPoints: OrderItem['paidWithPoints'];
    cart: OrderItem['cart'];
    orderNumber: string;
    orderStatus: string;
    delivery: Delivery | null;
    orderComment: string;
    hasPromo: boolean;
    promoCode?: string | { code?: string } | null;
    threePlusPromo?: ThreePlusPromoMeta | null;
    secondPillowPromo?: SecondPillowPromoMeta | null;
    group?: {
        id: string;
        name: string;
        url: string;
        optionValue: string;
    } | null;
    onEdit: (orderData: {
        id: string;
        user: OrderItem['user'];
        orderNumber: string;
        orderStatus: string;
        delivery: Delivery | null;
        timestamp: OrderItem['timestamp'];
        totalPrice: number;
        cart: OrderItem['cart'];
        totalDiscount: number;
        totalWeight: number;
        birthdayDiscount: boolean;
        paidWithPoints: number | null;
        orderComment: string;
        hasPromo: boolean;
        threePlusPromo?: ThreePlusPromoMeta | null;
        secondPillowPromo?: SecondPillowPromoMeta | null;
        // promoCode?: string | { code?: string } | null;
    }) => void;
    selectable?: boolean;
    selected?: boolean;
    onSelectChange?: (checked: boolean) => void;
}
type SecondPillowFields = {
    secondPillowAppliedQty?: unknown;
    secondPillowDiscountPercent?: unknown;
    secondPillowDiscountAmount?: unknown;
};

const getSizeOrColorLabel = (group?: CartItem['group']): string => {
    if (!group) return '';
    const value = (group.optionValue ?? '').trim();
    if (value) return value;
    const url = (group.url ?? '').trim();
    const name = (group.name ?? '').trim();
    const lowerUrl = url.toLowerCase();
    const lowerName = name.toLowerCase();
    if (lowerUrl === 'размер' || lowerUrl === 'цвет') return url;
    if (lowerName === 'размер' || lowerName === 'цвет') return name;
    return '';
};

const normalizeScan = (v: string): string => {
    const s = String(v ?? '')
        .replace(/\r?\n/g, '')
        .trim();
    return s.startsWith(']d2') ? s.slice(3) : s;
};
type MarkingAwareCartItem = CartItem & {
    lineId?: string;
    markingCodes?: string[] | null;
};
const readLineId = (item: unknown): string => {
    if (!item || typeof item !== 'object') return '';
    if (!('lineId' in item)) return '';
    const v = (item as { lineId?: unknown }).lineId;
    return typeof v === 'string' ? v : '';
};
const readMarkingCodes = (item: unknown): string[] => {
    if (!item || typeof item !== 'object') return [];
    const it = item as { markingCodes?: unknown };
    if (Array.isArray(it.markingCodes)) {
        return it.markingCodes
            .filter((x): x is string => typeof x === 'string')
            .map((x) => normalizeScan(x))
            .filter((x) => x.length > 0);
    }
    return [];
};
const readQuantity = (item: unknown): number => {
    if (!item || typeof item !== 'object') return 0;
    const v = (item as { quantity?: unknown }).quantity;
    const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;
    return Number.isFinite(n) ? Math.max(0, Math.trunc(n)) : 0;
};
// const readSecondPillowMetaFromCartItem = (cartItem: unknown): { appliedQty: number; discountPercent: number | null; discountAmount: number } => {
//     // Приоритет: cartItem.group.*, fallback: cartItem.*
//     const obj = cartItem && typeof cartItem === 'object' ? (cartItem as any) : null;
//     const group = obj?.group && typeof obj.group === 'object' ? (obj.group as any) : null;
//     const src: SecondPillowFields = (group ?? obj ?? {}) as any;
//     const aqRaw = src.secondPillowAppliedQty;
//     const aq = typeof aqRaw === 'number' ? aqRaw : typeof aqRaw === 'string' ? Number(aqRaw) : NaN;
//     const appliedQty = Number.isFinite(aq) ? Math.max(0, Math.trunc(aq)) : 0;
//     const pRaw = src.secondPillowDiscountPercent;
//     const p = typeof pRaw === 'number' ? pRaw : typeof pRaw === 'string' ? Number(pRaw) : NaN;
//     const discountPercent = Number.isFinite(p) && p > 0 ? Math.min(100, p) : null;
//     const aRaw = src.secondPillowDiscountAmount;
//     const a = typeof aRaw === 'number' ? aRaw : typeof aRaw === 'string' ? Number(aRaw) : NaN;
//     const discountAmount = Number.isFinite(a) ? Math.max(0, +a.toFixed(2)) : 0;
//     return { appliedQty, discountPercent, discountAmount };
// };
const readSecondPillowMetaFromCartItem = (cartItem: unknown): { appliedQty: number; discountPercent: number | null; discountAmount: number } => {
    const obj = cartItem && typeof cartItem === 'object' ? (cartItem as Record<string, unknown>) : null;
    const group = obj?.group && typeof obj.group === 'object' ? (obj.group as Record<string, unknown>) : null;
    const pickNum = (rootKey: keyof SecondPillowFields): number => {
        const rootVal = obj?.[rootKey as string];
        const groupVal = group?.[rootKey as string];
        const raw = rootVal ?? groupVal;
        const n = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : NaN;
        return Number.isFinite(n) ? n : NaN;
    };
    const aq = pickNum('secondPillowAppliedQty');
    const appliedQty = Number.isFinite(aq) ? Math.max(0, Math.trunc(aq)) : 0;
    const p = pickNum('secondPillowDiscountPercent');
    const discountPercent = Number.isFinite(p) && p > 0 ? Math.min(100, p) : null;
    const a = pickNum('secondPillowDiscountAmount');
    const discountAmount = Number.isFinite(a) ? Math.max(0, +a.toFixed(2)) : 0;
    return { appliedQty, discountPercent, discountAmount };
};
const isPromoEligibleOrderItem = (cartItem: CartItem): boolean => {
    const sp = readSecondPillowMetaFromCartItem(cartItem);
    const hasRegularDiscount = cartItem.discountedPrice != null;
    const hasSpecialDiscount = cartItem.specialDiscountedPrice != null;
    const hasThreePlus = cartItem.threePlusApplied === true;
    const hasSecondPillow = sp.appliedQty > 0 || sp.discountAmount > 0;

    return !hasRegularDiscount && !hasSpecialDiscount && !hasThreePlus && !hasSecondPillow;
};

const Order: React.FC<IOrderProps> = ({
    id,
    user,
    orderNumber,
    orderStatus,
    delivery,
    orderComment,
    timestamp,
    totalPrice,
    cart,
    totalDiscount,
    totalWeight,
    birthdayDiscount,
    paidWithPoints,
    hasPromo,
    promoCode,
    threePlusPromo,
    secondPillowPromo,
    // group,
    onEdit,
    selectable = false,
    selected = false,
    onSelectChange,
}) => {
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [currentOrderStatus, setCurrentOrderStatus] = useState(orderStatus);
    const [selectedStatus, setSelectedStatus] = useState(orderStatus);
    const dispatch = useDispatch<AppDispatch>();
    const { name: displayStatus, style: statusStyle } = getOrderStatusData(currentOrderStatus);
    // const date = new Date(timestamp).toLocaleDateString('Ru-ru');
    const dateTime = new Date(timestamp).toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });

    const stLower = (currentOrderStatus ?? '').toLowerCase();
    const isCancelled = stLower === 'cancelled';
    const isCompleted = stLower === 'completed';
    const isPending = stLower === 'pending';
    const isCreatingLike = stLower === 'creating' || stLower === 'draft' || stLower === 'new';

    const canShowMarkingButton = !isCancelled && !isCompleted && !isPending && !isCreatingLike;

    const addressRaw = user.address ? user.address.raw : '';
    const [articlesMap, setArticlesMap] = useState<Record<string, string>>({});
    const API_BASE = process.env.REACT_APP_SERVER_URL || 'http://localhost:5000';

    const [promoPercent, setPromoPercent] = useState<number | null>(null);
    const [promoLoaded, setPromoLoaded] = useState<boolean>(false);
    const [promoLoading, setPromoLoading] = useState<boolean>(false);

    const [isMarkingOpen, setIsMarkingOpen] = useState<boolean>(false);

    const normalizePromoCode = (p?: string | { code?: string } | null): string => {
        if (!p) return '';
        if (typeof p === 'string') return p.trim().toUpperCase();
        const c = (p.code ?? '').trim();
        return c.toUpperCase();
    };

    const promoCodeUpper = normalizePromoCode(promoCode);

    const clampPercent = (val: number): number => {
        if (!Number.isFinite(val)) return 0;
        if (val < 0) return 0;
        if (val > 100) return 100;
        return Math.round(val * 100) / 100;
    };
    const safeParseNumber = (v: number | string): number => {
        const n = typeof v === 'string' ? Number(v) : v;
        return Number.isFinite(n) ? n : NaN;
    };

    const deliveryLabel = useMemo(() => {
        const m = String(delivery?.method ?? '').toLowerCase();
        if (m === 'dtl') return 'Доставка ДТЛ';
        if (!m) return '';
        return `Доставка ${m.toUpperCase()}`;
    }, [delivery?.method]);
    const deliveryTrackText = useMemo(() => {
        if (!delivery) return '';
        const m = String(delivery.method ?? '').toLowerCase();
        if (m === 'dtl') return 'Бесплатная доставка ДТЛ';
        const orderNum = delivery.orderNum ? String(delivery.orderNum) : '';
        if (!orderNum) return '';
        return `Трек ${m.toUpperCase()} ${orderNum}`;
    }, [delivery]);

    const fetchPromocodesList = async (): Promise<PromoCodeRecord[]> => {
        const tryOnce = async (withCsrf: boolean): Promise<Response> => {
            const headers: HeadersInit = { 'X-Requested-With': 'XMLHttpRequest' };
            if (withCsrf) {
                try {
                    const csrf = await getCsrfToken();
                    (headers as Record<string, string>)['csrf-token'] = csrf;
                } catch {
                    // не падаем
                }
            }
            return fetch(`${API_BASE}/promocodes`, {
                method: 'GET',
                credentials: 'include',
                headers,
            });
        };
        let resp = await tryOnce(false);
        if (resp.status === 401 || resp.status === 403) {
            resp = await tryOnce(true);
        }
        if (!resp.ok) {
            throw new Error(`Ошибка запроса промокодов: ${resp.status} ${resp.statusText}`);
        }
        const data = (await resp.json()) as unknown;
        if (!Array.isArray(data)) {
            throw new Error('Некорректный формат ответа /promocodes');
        }
        const result: PromoCodeRecord[] = data
            .map((d) => ({
                code: typeof d?.code === 'string' ? d.code : '',
                discountType: d?.discountType === 'percentage' || d?.discountType === 'points' ? d.discountType : 'points',
                discountValue: typeof d?.discountValue === 'number' || typeof d?.discountValue === 'string' ? d.discountValue : 0,
                status: d?.status === 'active' || d?.status === 'inactive' ? d.status : undefined,
            }))
            .filter((d) => d.code.trim().length > 0);
        return result;
    };

    const resolvePercentagePromo = async (code: string): Promise<number | null> => {
        if (!code) return null;
        const list = await fetchPromocodesList();
        const found = list.find((p) => p.code.toUpperCase() === code);
        if (!found) return null;
        if (found.status && found.status !== 'active') return null;
        if (found.discountType !== 'percentage') return null;
        const raw = safeParseNumber(found.discountValue);
        if (!Number.isFinite(raw) || raw <= 0) return null;
        return clampPercent(raw);
    };

    const loadPromoIfNeeded = async (): Promise<void> => {
        if (promoLoaded || promoLoading) return;
        if (!hasPromo) return;
        if (!promoCodeUpper) {
            console.log(`[Order ${orderNumber}] Промокод отсутствует в данных заказа.`);
            setPromoLoaded(true);
            setPromoPercent(null);
            return;
        }
        try {
            setPromoLoading(true);
            // console.log(`[Order ${orderNumber}] Загрузка процента по промокоду ${promoCodeUpper}...`);
            const percent = await resolvePercentagePromo(promoCodeUpper);
            setPromoPercent(percent);
            setPromoLoaded(true);
            // console.log(
            //     percent != null
            //         ? `[Order ${orderNumber}] Промокод ${promoCodeUpper}: ${percent}%`
            //         : `[Order ${orderNumber}] Промокод ${promoCodeUpper}: не найден или не процентный`
            // );
        } catch (e) {
            console.error(`[Order ${orderNumber}] Не удалось получить процент промокода`, e);
            setPromoLoaded(true);
            setPromoPercent(null);
        } finally {
            setPromoLoading(false);
        }
    };

    const toggle = () => {
        setIsCollapsed((prev) => {
            const next = !prev;
            if (next) {
                // if (hasPromo) {
                //     console.log(`[Order ${orderNumber}] Открытие. Промокод: ${promoCodeUpper || '(нет кода)'}`);
                // }
                void loadPromoIfNeeded();
            }
            return next;
        });
    };

    const handleUpdateStatus = () => {
        dispatch(updateOrderStatus({ id, orderStatus: selectedStatus }))
            .unwrap()
            .then((result) => {
                setCurrentOrderStatus(result.orderStatus);
                console.log('Статус успешно обновлён');
            })
            .catch((error) => {
                console.error(error);
                console.log('Ошибка при обновлении статуса');
            });
    };

    const handleEditClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        onEdit({
            id,
            user,
            orderNumber,
            orderStatus,
            delivery,
            orderComment,
            timestamp,
            totalPrice,
            cart,
            totalDiscount,
            totalWeight,
            birthdayDiscount,
            paidWithPoints,
            hasPromo,
            threePlusPromo,
            secondPillowPromo,
            // promoCode,
        });
    };

    const handleOpenMarking = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        setIsMarkingOpen(true);
    };
    const handleCloseMarking = () => {
        setIsMarkingOpen(false);
    };
    const fullyMarked = useMemo(() => {
        return cart.every((it) => {
            const qty = readQuantity(it);
            const codes = readMarkingCodes(it);
            if (qty <= 0) return true;
            return codes.length === qty;
        });
    }, [cart]);
    const markingRows: OrderMarkingRow[] = useMemo(() => {
        const rows = cart.map((cartItem, cartIndex) => {
            const sizeOrColor = getSizeOrColorLabel((cartItem as MarkingAwareCartItem).group);
            const lineId = readLineId(cartItem);
            const quantity = readQuantity(cartItem);
            return {
                cartIndex,
                lineId: lineId || undefined,
                quantity,
                name: (cartItem as MarkingAwareCartItem).name ?? '',
                articleNumber: articlesMap[(cartItem as MarkingAwareCartItem).productId] ?? '',
                sizeOrColor,
                initialMarkingCodes: readMarkingCodes(cartItem),
            };
        });
        rows.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'ru-RU', { sensitivity: 'base' }));
        return rows;
    }, [cart, articlesMap]);

    // Сумма скидки по акции 1+1 подушки -50% (meta -> fallback по cart)
    const secondPillowDiscountTotal = useMemo(() => {
        const metaVal = secondPillowPromo?.discountAmount;
        const metaAmount = typeof metaVal === 'number' && Number.isFinite(metaVal) ? Math.max(0, +metaVal.toFixed(2)) : 0;
        if (metaAmount > 0) return metaAmount;
        // fallback: суммируем по позициям
        const sum = cart.reduce((acc, item) => {
            const { discountAmount } = readSecondPillowMetaFromCartItem(item);
            return acc + discountAmount;
        }, 0);
        return Math.max(0, +sum.toFixed(2));
    }, [cart, secondPillowPromo]);
    const hasSecondPillowPromo = secondPillowDiscountTotal > 0;

    useEffect(() => {
        if (!isCollapsed) return;
        const ids = Array.from(new Set(cart.map((c) => c.productId).filter((v): v is string => typeof v === 'string' && v.trim().length > 0)));
        const missing = ids.filter((id) => !(id in articlesMap));
        if (missing.length === 0) return;
        let aborted = false;
        (async () => {
            try {
                const csrf = await getCsrfToken();
                const resp = await fetch(`${API_BASE}/products/articles`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                        'csrf-token': csrf,
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                    body: JSON.stringify({ ids: missing }),
                });
                if (!resp.ok) {
                    throw new Error(`Ошибка запроса артикула: ${resp.status} ${resp.statusText}`);
                }
                const items: Array<{ _id: string; articleNumber?: string }> = await resp.json();
                if (aborted) return;
                setArticlesMap((prev) => {
                    const next = { ...prev };
                    for (const it of items) {
                        next[it._id] = (it.articleNumber ?? '').toString();
                    }
                    for (const id of missing) {
                        if (!(id in next)) next[id] = '';
                    }
                    return next;
                });
            } catch (e) {
                if (!aborted) {
                    console.error('Не удалось получить артикулы заказа', e);
                }
            }
        })();
        return () => {
            aborted = true;
        };
    }, [isCollapsed, cart, articlesMap, API_BASE]);

    useEffect(() => {
        if (!isCollapsed) return;
        void loadPromoIfNeeded();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCollapsed]);

    return (
        <>
            <tr onClick={toggle} className={classes['summary-row']}>
                <td className={classes['sumarry-cell']} onClick={(e) => e.stopPropagation()}>
                    {selectable ? (
                        <input
                            type="checkbox"
                            checked={selected}
                            onChange={(e) => onSelectChange?.(e.target.checked)}
                            title="Добавить заказ в очередь на выгрузку"
                        />
                    ) : null}
                </td>
                <td className={classes['sumarry-cell']}>{orderNumber}</td>
                <td className={`${classes['sumarry-cell']}`}>
                    <span style={statusStyle} className={`${classes['orgerRow__elem_status']}`}>
                        {displayStatus}
                    </span>
                </td>
                <td className={classes['sumarry-cell']}>{dateTime}</td>
                <td className={classes['sumarry-cell']}>{user.name}</td>
                <td className={classes['sumarry-cell']}>{user.phone}</td>
                <td className={classes['sumarry-cell']}>{addressRaw}</td>
                <td className={classes['sumarry-cell']}>{totalPrice} ₽</td>
            </tr>

            {isCollapsed && (
                <>
                    <tr>
                        <td colSpan={8} className={classes['order-content']}>
                            <div className={classes['order-content__header']}>
                                <div className={classes['order-content__header_container']}>
                                    <span className={classes['order-content__header_title']}>Состав заказа {orderNumber}</span>

                                    <select
                                        className={classes['order-content__header_status']}
                                        value={selectedStatus}
                                        onChange={(e) => setSelectedStatus(e.target.value)}
                                        disabled={isCancelled}
                                    >
                                        {orderStatuses.map((status, index) => {
                                            return (
                                                <option key={`${status.id}-${index}`} value={status.id}>
                                                    {status.name}
                                                </option>
                                            );
                                        })}
                                    </select>

                                    {fullyMarked ? (
                                        <img
                                            src="/icons/logo-crpt.png"
                                            alt="Честный знак"
                                            title="Заказ промаркирован и готов к отправке"
                                            style={{ width: 16, height: 16, opacity: 0.9 }}
                                        />
                                    ) : null}

                                    {deliveryTrackText ? <span className={classes['order-content__header_title']}>{deliveryTrackText}</span> : null}
                                    {selectedStatus !== currentOrderStatus && (
                                        <button
                                            className={classes['order-content__header_btn']}
                                            type="button"
                                            title="Сохранить статус заказа"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleUpdateStatus();
                                            }}
                                        >
                                            Сохранить
                                        </button>
                                    )}
                                </div>

                                {canShowMarkingButton ? (
                                    <button
                                        className={classes['order-content__header_btn']}
                                        type="button"
                                        title="Маркировка (Честный знак)"
                                        onClick={handleOpenMarking}
                                    >
                                        Маркировка
                                    </button>
                                ) : null}
                                <button
                                    className={classes['order-content__header_btn']}
                                    type="button"
                                    title="Редактировать заказ"
                                    disabled={isCancelled || isCompleted} // запрещаем редактирование завершённых и отменённых заказов
                                    onClick={handleEditClick}
                                >
                                    Редактировать
                                </button>
                            </div>
                            <div className={classes['order-content__box']}>
                                <table className={classes['orderTable']}>
                                    <thead className={classes['orderTable__head']}>
                                        <tr className={`${classes['orderTable__head_row']} ${classes['no-pointer-events']}`}>
                                            <th className={classes['orderTable__head_row_elem']}>№</th>
                                            <th className={classes['orderTable__head_row_elem']}>Наименование</th>
                                            <th className={classes['orderTable__head_row_elem']}>Артикул</th>
                                            <th className={classes['orderTable__head_row_elem']}>Размер/Цвет</th>
                                            <th className={classes['orderTable__head_row_elem']}>Количество</th>
                                            <th className={classes['orderTable__head_row_elem']}>Доступно</th>
                                            <th className={classes['orderTable__head_row_elem']}>Цена</th>
                                            <th className={classes['orderTable__head_row_elem']}>Итого</th>
                                        </tr>
                                    </thead>
                                    <tbody className={classes['orderTable__body']}>
                                        {cart.map((cartItem, idx) => {
                                            const sizeOrColor = getSizeOrColorLabel(cartItem.group);
                                            const promoEligible = isPromoEligibleOrderItem(cartItem);
                                            // const sp = readSecondPillowMetaFromCartItem(cartItem);
                                            // const spHit = sp.appliedQty > 0 || sp.discountAmount > 0;
                                            // const spTitle = spHit ? `Акция 1+1 (подушки -50%): -${sp.discountAmount} ₽` : '';
                                            return (
                                                <OrderCartItem
                                                    key={cartItem.productId}
                                                    idx={idx + 1}
                                                    name={cartItem.name}
                                                    quantity={cartItem.quantity}
                                                    stockQuantity={cartItem.stockQuantity}
                                                    price={cartItem.price}
                                                    discount={cartItem.discount}
                                                    discountedPrice={cartItem.discountedPrice}
                                                    specialDiscountedPrice={cartItem.specialDiscountedPrice}
                                                    specialDiscountPercent={cartItem.specialDiscountPercent}
                                                    threePlusApplied={cartItem.threePlusApplied === true}
                                                    // totalPrice={cartItem.totalPrice}
                                                    birthdayDiscount={birthdayDiscount}
                                                    hasPromo={hasPromo && promoEligible && promoPercent != null}
                                                    articleNumber={articlesMap[cartItem.productId] ?? ''}
                                                    promoPercent={promoEligible ? promoPercent : null}
                                                    sizeOrColor={sizeOrColor}
                                                    // secondPillowAppliedQty={sp.appliedQty}
                                                    // secondPillowDiscountPercent={sp.discountPercent}
                                                    // secondPillowDiscountAmount={sp.discountAmount}
                                                    // rowHighlight={spHit}
                                                    // rowHighlightTitle={spTitle}
                                                />
                                            );
                                        })}

                                        <tr className={`${classes['orderTable__body_row']} ${classes['no-pointer-events']}`}>
                                            <td colSpan={7}></td>
                                        </tr>
                                    </tbody>
                                </table>

                                <div className={classes['orderResult']}>
                                    <div className={classes['orderResult__box']}>
                                        <span className={classes['orderResult__box_label']}>Общая масса:</span>
                                        <span className={classes['orderResult__box_item']}>{totalWeight} кг</span>
                                    </div>
                                    <div className={classes['orderResult__box']}>
                                        <span className={classes['orderResult__box_label']} title="Скидка 25%">
                                            Скидка в день рождения:
                                        </span>
                                        <span className={classes['orderResult__box_item']}>{birthdayDiscount ? 'да' : 'нет'}</span>
                                    </div>
                                    <div className={classes['orderResult__box']}>
                                        <span className={classes['orderResult__box_label']}>Промо-код:</span>
                                        <span className={classes['orderResult__box_item']}>{hasPromo ? 'да' : 'нет'}</span>
                                    </div>
                                    <div className={classes['orderResult__box']}>
                                        <span className={classes['orderResult__box_label']}>Баллов потрачено:</span>
                                        <span className={classes['orderResult__box_item']}>{paidWithPoints ? `${formatPrice(paidWithPoints)} б` : '0'}</span>
                                    </div>
                                    {threePlusPromo && (
                                        <div className={classes['orderResult__box']}>
                                            <span
                                                className={classes['orderResult__box_label']}
                                                title={`Акция 3+ товаров, ${threePlusPromo.validUntil ? ` до ${threePlusPromo.validUntil}` : ''}`}
                                            >
                                                Акция 3+:
                                            </span>
                                            <span className={classes['orderResult__box_item']}>{formatPrice(threePlusPromo.discountAmount)} ₽</span>
                                        </div>
                                    )}
                                    {hasSecondPillowPromo ? (
                                        <div className={classes['orderResult__box']}>
                                            <span
                                                className={classes['orderResult__box_label']}
                                                title={
                                                    secondPillowPromo?.validUntil
                                                        ? `Акция 1+1 подушки -50% до ${secondPillowPromo.validUntil}`
                                                        : 'Акция 1+1 подушки -50%'
                                                }
                                            >
                                                Акция 1+1 (подушки -50%):
                                            </span>
                                            <span className={classes['orderResult__box_item']}>{formatPrice(secondPillowDiscountTotal)} ₽</span>
                                        </div>
                                    ) : null}
                                    <div className={classes['orderResult__box']}>
                                        <span className={classes['orderResult__box_label']}>Общая скидка:</span>
                                        <span className={classes['orderResult__box_item']}>{formatPrice(totalDiscount)} ₽</span>
                                    </div>
                                    {delivery && (
                                        <div className={classes['orderResult__box']}>
                                            <span className={classes['orderResult__box_label']}>{deliveryLabel}:</span>
                                            <span className={classes['orderResult__box_item']}>{formatPrice(delivery.cost)} ₽</span>
                                        </div>
                                    )}
                                    <div className={classes['orderResult__box']}>
                                        <span className={classes['orderResult__box_label']}>Итоговая цена:</span>
                                        <span className={classes['orderResult__box_item']}>{formatPrice(totalPrice)} ₽</span>
                                    </div>
                                </div>
                            </div>
                        </td>
                    </tr>
                    <tr className={classes['no-pointer-events']}>
                        <td colSpan={8}></td>
                    </tr>
                    <tr className={classes['no-pointer-events']}>
                        <td colSpan={8}></td>
                    </tr>
                    {isMarkingOpen ? (
                        <OrderMarkingPopup
                            orderId={id}
                            orderNumber={orderNumber}
                            apiBase={API_BASE}
                            rows={markingRows}
                            onClose={handleCloseMarking}
                            onSaved={(result) => {
                                if (result?.orderStatus) setCurrentOrderStatus(result.orderStatus);
                                dispatch(fetchOrders());
                            }}
                        />
                    ) : null}
                </>
            )}
        </>
    );
};

export default Order;
